import React, { useEffect, useRef, useState } from 'react';
import styles from './SignaturePad.module.css';

// Inline canvas signature pad. Pointer Events so mouse, touch, and pen all
// drive the same code path. Stores strokes as paths and re-renders on
// resize so the captured PNG always matches the visible drawing.
//
// Usage:
//   <SignaturePad value={dataUrl} onChange={setDataUrl} disabled={readOnly} />
//
// `value` is a base64 PNG data URL ('data:image/png;base64,...') or null.
// We keep the canvas in sync with the prop on first mount + when value
// is cleared externally.

export default function SignaturePad({ value, onChange, disabled, height = 140 }) {
  const wrapRef   = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef    = useRef(null);
  const strokesRef = useRef([]);     // [{ pts: [{x,y}, ...] }]
  const [hasInk, setHasInk] = useState(Boolean(value));

  // Resize the canvas to its parent's pixel size and re-stroke. devicePixelRatio
  // keeps the line crisp on retina screens.
  function resizeAndRedraw() {
    const wrap = wrapRef.current;
    const cvs  = canvasRef.current;
    if (!wrap || !cvs) return;
    const dpr = window.devicePixelRatio || 1;
    const w = wrap.clientWidth;
    const h = height;
    cvs.width  = w * dpr;
    cvs.height = h * dpr;
    cvs.style.width  = `${w}px`;
    cvs.style.height = `${h}px`;
    const ctx = cvs.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#111';
    for (const s of strokesRef.current) {
      if (s.pts.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(s.pts[0].x, s.pts[0].y);
      for (let i = 1; i < s.pts.length; i++) ctx.lineTo(s.pts[i].x, s.pts[i].y);
      ctx.stroke();
    }
  }

  useEffect(() => {
    resizeAndRedraw();
    const onResize = () => resizeAndRedraw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external clear: parent sets value=null → wipe local strokes.
  useEffect(() => {
    if (!value) {
      strokesRef.current = [];
      setHasInk(false);
      resizeAndRedraw();
    }
    // We don't restore strokes from a saved data URL — once it's a flat PNG
    // it's authoritative; no per-stroke editing after the fact.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function pointFromEvent(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function commit() {
    const cvs = canvasRef.current;
    if (!cvs) return;
    if (!strokesRef.current.length) {
      onChange?.(null);
      setHasInk(false);
      return;
    }
    const dataUrl = cvs.toDataURL('image/png');
    onChange?.(dataUrl);
    setHasInk(true);
  }

  function onPointerDown(e) {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const p = pointFromEvent(e);
    lastRef.current = p;
    strokesRef.current.push({ pts: [p] });
  }
  function onPointerMove(e) {
    if (!drawingRef.current) return;
    const p = pointFromEvent(e);
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    stroke.pts.push(p);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#111';
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  }
  function onPointerUp() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    commit();
  }

  function clear() {
    strokesRef.current = [];
    resizeAndRedraw();
    onChange?.(null);
    setHasInk(false);
  }

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className={`${styles.canvas} ${disabled ? styles.disabled : ''} ${hasInk ? styles.signed : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{ touchAction: 'none' }}
      />
      <div className={styles.bar}>
        {!hasInk && <span className={styles.hint}>Sign with your finger or mouse</span>}
        {hasInk && <span className={styles.hint}>Signed</span>}
        {!disabled && (
          <button type="button" className={styles.clearBtn} onClick={clear}>Clear</button>
        )}
      </div>
    </div>
  );
}
